import { mergeClasses } from '@fluentui/react-components';
import { useState } from 'react';
import {
  Button,
  Card,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Info16Regular } from '@fluentui/react-icons';
import { Take5Submission, SiteContact } from '../types';
import { PhaseStrip } from './PhaseStrip';
import { TapRow } from './TapRow';

interface Props {
  initial: Partial<Take5Submission['review']>;
  siteContact: SiteContact | null;
  onNext: (data: Take5Submission['review']) => void;
  onShowInfo: (key: string) => void;
}

interface Triggers {
  conditionsChange: boolean;
  newHazard: boolean;
  incidentOrNearMiss: boolean;
  scopeChanges: boolean;
}

const TRIGGERS: Array<{ key: keyof Triggers; label: string }> = [
  { key: 'conditionsChange', label: 'Conditions change unexpectedly (weather, access, materials)' },
  { key: 'newHazard', label: 'A new hazard is identified on site' },
  { key: 'incidentOrNearMiss', label: 'An incident or near-miss occurs' },
  { key: 'scopeChanges', label: 'The scope of work changes' },
];

function telHref(phone: string): string {
  return `tel:${phone.replace(/[^\d+]/g, '')}`;
}

const useStyles = makeStyles({
  body: { flex: 1, overflowY: 'auto', paddingTop: '16px', paddingBottom: '20px', paddingLeft: '18px', paddingRight: '18px' },
  section: {
    marginBottom: '14px',
    paddingTop: '16px',
    paddingBottom: '16px',
    paddingLeft: '16px',
    paddingRight: '16px',
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow2,
    background: tokens.colorNeutralBackground1,
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  stepNum: {
    backgroundColor: '#231F20',
    color: '#FFFFFF',
    width: '26px',
    height: '26px',
    borderRadius: '50%',
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    flexShrink: 0,
  },
  infoBtn: { marginLeft: 'auto', minWidth: '44px', minHeight: '44px' },
  banner: {
    borderRadius: '16px',
    paddingTop: '13px',
    paddingBottom: '13px',
    paddingLeft: '14px',
    paddingRight: '14px',
    fontSize: '13px',
    lineHeight: 1.5,
    marginBottom: '12px',
    backgroundColor: '#FFF8E6',
    borderLeftWidth: '3px',
    borderLeftStyle: 'solid',
    borderLeftColor: '#E65100',
    color: '#7A3C00',
    fontWeight: 500,
  },
  reportAlert: {
    borderRadius: '16px',
    paddingTop: '13px',
    paddingBottom: '13px',
    paddingLeft: '14px',
    paddingRight: '14px',
    fontSize: '13px',
    lineHeight: 1.5,
    marginBottom: '12px',
    backgroundColor: '#FCE8E9',
    borderLeftWidth: '3px',
    borderLeftStyle: 'solid',
    borderLeftColor: '#E3272D',
    color: '#6F0F12',
  },
  emergencyCard: {
    backgroundColor: '#FCE8E9',
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: '#F15A5F',
    borderRightColor: '#F15A5F',
    borderBottomColor: '#F15A5F',
    borderLeftColor: '#F15A5F',
    paddingTop: '16px',
    paddingBottom: '16px',
    paddingLeft: '16px',
    paddingRight: '16px',
    marginBottom: '12px',
    borderRadius: tokens.borderRadiusLarge,
  },
  emergencyTitle: {
    fontSize: '14px',
    fontWeight: 700,
    color: '#6F0F12',
    marginBottom: '10px',
  },
  emergencyRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: '8px',
    paddingBottom: '8px',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: '#F5C8CA',
    fontSize: '13.5px',
    minHeight: '40px',
    ':last-child': { borderBottomStyle: 'none' },
  },
  emergencyKey: { color: '#6F0F12', fontWeight: 600 },
  emergencyVal: {
    fontWeight: 700,
    fontFamily: 'ui-monospace, Menlo, monospace',
  },
  emergencyLink: {
    color: '#6F0F12',
    textDecoration: 'underline',
  },
  emergencyLegal: {
    fontSize: '11px',
    color: '#6F0F12',
    marginTop: '10px',
    lineHeight: 1.5,
    fontStyle: 'italic',
  },
  cta: {
    width: '100%',
    marginTop: '8px',
    minHeight: '52px',
    fontSize: '15px',
    fontWeight: 600,
    borderRadius: '18px',
    boxShadow: '0 10px 22px rgba(227,39,45,0.18)',
  },
});

export function ReviewScreen({ initial, siteContact, onNext, onShowInfo }: Props) {
  const styles = useStyles();
  const [triggers, setTriggers] = useState<Triggers>({
    conditionsChange: initial.reassessTriggers?.conditionsChange || false,
    newHazard: initial.reassessTriggers?.newHazard || false,
    incidentOrNearMiss: initial.reassessTriggers?.incidentOrNearMiss || false,
    scopeChanges: initial.reassessTriggers?.scopeChanges || false,
  });

  const toggle = (key: keyof Triggers) => {
    setTriggers(t => ({ ...t, [key]: !t[key] }));
  };

  const allChecked = Object.values(triggers).every(Boolean);
  const remainingCount = Object.values(triggers).filter(v => !v).length;

  const handleNext = () => {
    if (!allChecked) return;
    onNext({
      reassessTriggers: {
        conditionsChange: true,
        newHazard: true,
        incidentOrNearMiss: true,
        scopeChanges: true,
      },
    });
  };

  return (
    <>
      <PhaseStrip step={4} />
      <div className={styles.body} data-screen-body>
        <div className={styles.banner}>
          <strong>Required.</strong> Tap every trigger below to acknowledge your right to stop and re-assess. All four must be ticked to sign off.
        </div>

        <Card className={styles.section}>
          <div className={styles.sectionTitle}>
            <span className={styles.stepNum}>7</span>
            I will stop & re-assess if…
            <Button
              appearance="subtle"
              icon={<Info16Regular />}
              onClick={() => onShowInfo('reassess')}
              className={styles.infoBtn}
              aria-label="More info"
            />
          </div>
          {TRIGGERS.map(t => (
            <TapRow
              key={t.key}
              checked={triggers[t.key]}
              onToggle={() => toggle(t.key)}
            >
              {t.label}
            </TapRow>
          ))}
        </Card>

        <div className={styles.reportAlert}>
          <strong>Reporting hazards.</strong> All hazards must be reported to the Supervisor, Manager or Safety and Health Representative.
        </div>

        <Card className={styles.emergencyCard}>
          <div className={styles.emergencyTitle}>⚠ Emergency & incident reporting</div>

          <div className={styles.emergencyRow}>
            <span className={styles.emergencyKey}>Emergency Services</span>
            <a className={mergeClasses(styles.emergencyVal, styles.emergencyLink)} href={telHref('000')}>000</a>
          </div>
          <div className={styles.emergencyRow}>
            <span className={styles.emergencyKey}>WorkSafe WA</span>
            <a className={mergeClasses(styles.emergencyVal, styles.emergencyLink)} href={telHref('1300 307 877')}>1300 307 877</a>
          </div>
          <div className={styles.emergencyRow}>
            <span className={styles.emergencyKey}>Harley's Plumbing Office</span>
            <a className={mergeClasses(styles.emergencyVal, styles.emergencyLink)} href={telHref('(08) 6201 8650')}>(08) 6201 8650</a>
          </div>
          <div className={styles.emergencyRow}>
            <span className={styles.emergencyKey}>Harley's Plumbing Team Priority</span>
            <a className={mergeClasses(styles.emergencyVal, styles.emergencyLink)} href={telHref('(08) 6205 4888')}>(08) 6205 4888</a>
          </div>
          {siteContact && (siteContact.fullName || siteContact.bestPhone) && (
            <div className={styles.emergencyRow}>
              <span className={styles.emergencyKey}>
                Site Contact{siteContact.fullName ? ` (${siteContact.fullName})` : ''}
              </span>
              {siteContact.bestPhone ? (
                <a className={mergeClasses(styles.emergencyVal, styles.emergencyLink)} href={telHref(siteContact.bestPhone)}>
                  {siteContact.bestPhone}
                </a>
              ) : (
                <span className={styles.emergencyVal}>—</span>
              )}
            </div>
          )}
          <div className={styles.emergencyRow}>
            <span className={styles.emergencyKey}>First Aid Officer</span>
            <span className={styles.emergencyVal}>On-site lead</span>
          </div>

          <div className={styles.emergencyLegal}>
            Notifiable incidents (death, serious injury, dangerous incident) MUST be reported to <a href="https://www.worksafe.wa.gov.au/" target="_blank" rel="noopener noreferrer">WorkSafe WA</a> immediately under WHS Act 2020 ss.38–39.
          </div>
        </Card>

        <Button
          appearance="primary"
          size="large"
          disabled={!allChecked}
          onClick={handleNext}
          className={styles.cta}
        >
          {allChecked ? 'Next · Sign off' : `Tap all ${remainingCount} remaining to continue`}
        </Button>
      </div>
    </>
  );
}
